import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
// Constants - Functions
import { WEB_SERVICE_MIMS } from "../../../../../../config/constants";
// Components
import {
  MainDiv,
  SectionTitle,
  FormSection,
  FormTitle,
  ColumnsDiv,
  ColumnDiv,
  InputDiv,
  Label,
  Input,
  ActionsDiv,
  CancelButton,
  SaveButton,
} from "./styles";
import UseFetch from "../../../../../../config/useFetch";
import Loader from "../../../../../loader";

const EditClinicalScore = ({
  patientData,
  setShowClinicalScoresList,
  clinicalScore,
  setShowEditClinicalScore,
  refreshClinicalScores,
  setRefreshClinicalScores,
}) => {
  const [title, setTitle] = useState("");
  const [disableSaveButton, setDisableSaveButton] = useState(false);
  // MoCA
  const [VisuospatialExecutive, setVisuospatialExecutive] = useState("");
  const [naming, setNaming] = useState("");
  const [attention, setAttention] = useState("");
  const [language, setLanguage] = useState("");
  const [abstraction, setAbstraction] = useState("");
  const [delayedRecall, setDelayedRecall] = useState("");
  const [orientation, setOrientation] = useState("");
  // ARAT
  const [grip, setGrip] = useState("");
  const [grossMovement, setGrossMovement] = useState("");
  const [pinch, setPinch] = useState("");
  const [grasp, setGrasp] = useState("");
  // Other
  const [depression, setDepression] = useState("");
  const [pain, setPain] = useState("");
  const [fatigue, setFatigue] = useState("");

  const { patientId } = useParams();

  // Set title
  useEffect(() => {
    const mapTitle = {
      pre: "Baseline",
      END_OF_THERAPY: "End of therapy",
      FOLLOW_UP: "Follow up",
    };
    setTitle(mapTitle[clinicalScore.KEY] || "");
    return () => {
      setTitle("");
    };
  }, [clinicalScore]);

  // Set each field
  useEffect(() => {
    // MoCA
    clinicalScore?.CLINICAL_SCORES?.MoCA?.hasOwnProperty("Visuospatial/Executive") &&
      setVisuospatialExecutive(clinicalScore.CLINICAL_SCORES.MoCA["Visuospatial/Executive"]);
    clinicalScore?.CLINICAL_SCORES?.MoCA?.hasOwnProperty("Naming") && setNaming(clinicalScore.CLINICAL_SCORES.MoCA.Naming);
    clinicalScore?.CLINICAL_SCORES?.MoCA?.hasOwnProperty("Attention") && setAttention(clinicalScore.CLINICAL_SCORES.MoCA.Attention);
    clinicalScore?.CLINICAL_SCORES?.MoCA?.hasOwnProperty("Language") && setLanguage(clinicalScore.CLINICAL_SCORES.MoCA.Language);
    clinicalScore?.CLINICAL_SCORES?.MoCA?.hasOwnProperty("Abstraction") && setAbstraction(clinicalScore.CLINICAL_SCORES.MoCA.Abstraction);
    clinicalScore?.CLINICAL_SCORES?.MoCA?.hasOwnProperty("DelayedRecall") &&
      setDelayedRecall(clinicalScore.CLINICAL_SCORES.MoCA.DelayedRecall);
    clinicalScore?.CLINICAL_SCORES?.MoCA?.hasOwnProperty("Orientation") && setOrientation(clinicalScore.CLINICAL_SCORES.MoCA.Orientation);

    // ARAT
    clinicalScore?.CLINICAL_SCORES?.ARAT?.hasOwnProperty("Grip") && setGrip(clinicalScore.CLINICAL_SCORES.ARAT.Grip);
    clinicalScore?.CLINICAL_SCORES?.ARAT?.hasOwnProperty("GrossMovement") &&
      setGrossMovement(clinicalScore.CLINICAL_SCORES.ARAT.GrossMovement);
    clinicalScore?.CLINICAL_SCORES?.ARAT?.hasOwnProperty("Pinch") && setPinch(clinicalScore.CLINICAL_SCORES.ARAT.Pinch);
    clinicalScore?.CLINICAL_SCORES?.ARAT?.hasOwnProperty("Grasp") && setGrasp(clinicalScore.CLINICAL_SCORES.ARAT.Grasp);
    // Other
    clinicalScore?.CLINICAL_SCORES?.Other?.hasOwnProperty("Depression") && setDepression(clinicalScore.CLINICAL_SCORES.Other.Depression);
    clinicalScore?.CLINICAL_SCORES?.Other?.hasOwnProperty("Pain") && setPain(clinicalScore.CLINICAL_SCORES.Other.Pain);
    clinicalScore?.CLINICAL_SCORES?.Other?.hasOwnProperty("Fatigue") && setFatigue(clinicalScore.CLINICAL_SCORES.Other.Fatigue);
  }, [clinicalScore]);

  const renderInput = (label, value, setter) => {
    const maxValues = {
      "Visuospatial / Executive Function": 5,
      Naming: 3,
      Attention: 6,
      Language: 3,
      Abstraction: 2,
      "Delayed Recall": 5,
      Orientation: 6,
      Grip: 12,
      "Gross Movement": 9,
      Pinch: 18,
      Grasp: 18,
      Depression: 52,
      Pain: 10,
      Fatigue: 63,
    };
    const currentMaxValue = maxValues[label];

    const handleSetNumber = (e) => {
      const value = Number(e.target.value);

      if (value > currentMaxValue) {
        setter(currentMaxValue);
      }

      if (value === "" || (value >= 0 && value <= currentMaxValue)) {
        setter(value);
      } else {
        toast.error(`The value for '${label}' must be between 0 and ${currentMaxValue}`);
      }
    };

    return (
      <InputDiv>
        <Label htmlFor={label}>{label}</Label>
        <Input id={label} type="number" value={value} onChange={handleSetNumber} />
      </InputDiv>
    );
  };

  const saveClinicalScore = () => {
    setDisableSaveButton(true);

    let moca = {};
    let arat = {};
    let other = {};

    // MoCA
    if (VisuospatialExecutive !== "") moca["Visuospatial/Executive"] = VisuospatialExecutive;
    if (naming !== "") moca.Naming = naming;
    if (attention !== "") moca.Attention = attention;
    if (language !== "") moca.Language = language;
    if (abstraction !== "") moca.Abstraction = abstraction;
    if (delayedRecall !== "") moca.DelayedRecall = delayedRecall;
    if (orientation !== "") moca.Orientation = orientation;
    // ARAT
    if (grip !== "") arat.Grip = grip;
    if (grossMovement !== "") arat.GrossMovement = grossMovement;
    if (pinch !== "") arat.Pinch = pinch;
    if (grasp !== "") arat.Grasp = grasp;
    // Other
    if (depression !== "") other.Depression = depression;
    if (pain !== "") other.Pain = pain;
    if (fatigue !== "") other.Fatigue = fatigue;

    if (!Object.values(moca).length && !Object.values(arat).length && !Object.values(other).length) {
      return toast.error("You must add at least one value");
    }

    let clinicalScoresObj = {};

    if (Object.values(moca).length) clinicalScoresObj.MoCA = moca;
    if (Object.values(arat).length) clinicalScoresObj.ARAT = arat;
    if (Object.values(other).length) clinicalScoresObj.Other = other;

    let ClinicalScoreToSave = {
      CLINICAL_TRIALS_ID: clinicalScore?.CLINICAL_TRIALS_ID ? clinicalScore?.CLINICAL_TRIALS_ID : 0,
      CLINICAL_SCORES: [
        {
          evaluation_date: moment(patientData.aisnData.ROUTINE_STARTING_DATE, "DD/MM/YYYY").format("YYYY-MM-DD"),
          condition: clinicalScore.KEY,
          ...clinicalScoresObj,
        },
      ],
    };

    UseFetch("post", `${WEB_SERVICE_MIMS}/recsys/add-clinical-score/${patientId}`, new AbortController(), ClinicalScoreToSave)
      .then(({ data }) => {
        if (data.clinicalScoreAdded) {
          setDisableSaveButton(false);
          toast.success("Clinical score saved successfully");
          setShowClinicalScoresList(false);
          setShowEditClinicalScore(false);
          setRefreshClinicalScores(!refreshClinicalScores);
        }
      })
      .catch((error) => {
        console.log("Add clinical score: ", error);
      });
  };

  return (
    <MainDiv>
      <SectionTitle>Edit clinical score | {title}</SectionTitle>
      {/* MoCA */}
      <FormSection margin="30px 0 16px 0">
        <FormTitle>MoCA</FormTitle>
        <ColumnsDiv>
          <ColumnDiv>
            {renderInput("Visuospatial / Executive Function", VisuospatialExecutive, setVisuospatialExecutive)}
            {renderInput("Naming", naming, setNaming)}
            {renderInput("Attention", attention, setAttention)}
            {renderInput("Language", language, setLanguage)}
          </ColumnDiv>
          <ColumnDiv>
            {renderInput("Abstraction", abstraction, setAbstraction)}
            {renderInput("Delayed Recall", delayedRecall, setDelayedRecall)}
            {renderInput("Orientation", orientation, setOrientation)}
          </ColumnDiv>
        </ColumnsDiv>
      </FormSection>
      {/* ARAT */}
      <FormSection margin="0 0 16px 0">
        <FormTitle>ARAT</FormTitle>
        <ColumnsDiv>
          <ColumnDiv>
            {renderInput("Grip", grip, setGrip)}
            {renderInput("Gross Movement", grossMovement, setGrossMovement)}
          </ColumnDiv>
          <ColumnDiv>
            {renderInput("Pinch", pinch, setPinch)}
            {renderInput("Grasp", grasp, setGrasp)}
          </ColumnDiv>
        </ColumnsDiv>
      </FormSection>
      {/* Other */}
      <FormSection margin="0 0 30px 0">
        <FormTitle>Other</FormTitle>
        <ColumnsDiv>
          <ColumnDiv>
            {renderInput("Depression", depression, setDepression)}
            {renderInput("Pain", pain, setPain)}
          </ColumnDiv>
          <ColumnDiv>{renderInput("Fatigue", fatigue, setFatigue)}</ColumnDiv>
        </ColumnsDiv>
      </FormSection>
      <ActionsDiv>
        <CancelButton onClick={() => setShowEditClinicalScore(false)}>Cancel</CancelButton>
        <SaveButton onClick={saveClinicalScore} disabled={disableSaveButton}>
          {disableSaveButton ? <Loader height={20} color="#fff" /> : "Save changes"}
        </SaveButton>
      </ActionsDiv>
    </MainDiv>
  );
};

export default EditClinicalScore;
